import { CalendarToday } from '@workwave-tidal/core/icons'
import { preview } from '@/components/form/preview'
import { useOrderViolationTexts } from '@/intl'
import { formatDate, useFormatRecurrence, useFormatEligibility } from '@/hooks'
import { Stack, Typography, Link } from '@mui/material'
import { FiberManualRecord, Repeat } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { selectRecurrences } from '@/features/domain/routeplan'
import { selectUserConfiguration } from '@/features/domain/user'
import { getRecurrence } from '@/local/server-data/domain/server/factories/formattedData'
import * as Texts from '../../../../../components/ViewDetails/intl'
import * as OrderTexts from '../../../../../intl'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
  showOrderPreviewModal?: (id: string, date: string) => void
  getRecurrentOrder?: (id: string, date: string) => Promise<void>
  isOpenedInOrderPreviewModal?: boolean
}

type ReccurentOrderDateProps = {
  label: string
  linkText: React.ReactNode
  onClick?: () => void
  disabled: boolean
  testId?: string
  isOpenedInOrderPreviewModal?: boolean
}

export function Eligibility(props: Props) {
  const {
    extOrder,
    showOrderPreviewModal,
    getRecurrentOrder,
    isOpenedInOrderPreviewModal = false,
  } = props

  // Extract data from extOrder
  const eligibilityData = extOrder.order?.eligibility
  const violations =
    !extOrder.isUnassigned && extOrder?.violations ? [...new Set(extOrder.violations)] : []
  const recurrenceId = extOrder.order?.recurrenceId
  const previousRecurrentOrder = extOrder.order?.previousRecurrentOrder
  const nextRecurrentOrder = extOrder.order?.nextRecurrentOrder

  const formatRecurrence = useFormatRecurrence()
  const formatEligibility = useFormatEligibility()
  const { getViolationText } = useOrderViolationTexts()

  // Format eligibility for display
  const eligibility = eligibilityData ? formatEligibility(eligibilityData) : <preview.NullValue />

  const recurrences: Record<string, uui.domain.client.rm.Recurrence> =
    useSelector(selectRecurrences)
  const selectedRecurrence = getRecurrence(recurrences, recurrenceId)
  const userConfiguration = useSelector(selectUserConfiguration)

  const eligibilityViolation: boolean = violations.includes('ELG')

  const prevLinkText = previousRecurrentOrder ? (
    formatDate(previousRecurrentOrder.date, false, userConfiguration)
  ) : (
    <preview.NullValue />
  )

  const nextLinkText = nextRecurrentOrder ? (
    formatDate(nextRecurrentOrder.date, false, userConfiguration)
  ) : (
    <preview.NullValue />
  )

  const getTestId = (baseId: string) =>
    isOpenedInOrderPreviewModal ? `orderPreviewModal__${baseId}` : baseId

  const handleRecurrentClick = (recurrentOrder: uui.domain.server.rm.RecurrenceElement) => {
    if (!recurrentOrder.orderId) return

    if (getRecurrentOrder) {
      getRecurrentOrder(recurrentOrder.orderId, recurrentOrder.date)
    }

    if (showOrderPreviewModal && !isOpenedInOrderPreviewModal) {
      showOrderPreviewModal(recurrentOrder.orderId, recurrentOrder.date)
    }
  }

  return (
    <Stack spacing={1}>
      <preview.Field label={Texts.getEligibilityText()}>
        <preview.ViolationContainer
          violation={
            eligibilityViolation ? (
              <preview.ViolationBadge tooltip={getViolationText('ELG')} />
            ) : null
          }
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <CalendarToday sx={{ color: t => t.vars.palette.action.active, width: 20 }} />
            <Typography data-testid={getTestId('Eligibility-Value')}>{eligibility}</Typography>
          </Stack>
        </preview.ViolationContainer>
      </preview.Field>

      {recurrenceId && selectedRecurrence ? (
        <preview.Value>
          <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              data-testid={getTestId('Recurrence')}
            >
              <Repeat sx={{ fontSize: 16, color: t => t.vars.palette.action.active }} />
              <Typography data-testid={getTestId('Recurrence-Format')} fontSize={12}>
                {formatRecurrence(selectedRecurrence)}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid={getTestId('PreviousNextRecurrences')}
              sx={{ paddingLeft: '24px' }}
            >
              {previousRecurrentOrder && (
                <ReccurentOrderDate
                  testId="PreviousRecurrence"
                  label={OrderTexts.getPreviousReccurentOrderLabelText()}
                  linkText={prevLinkText}
                  onClick={() => handleRecurrentClick(previousRecurrentOrder)}
                  disabled={!previousRecurrentOrder.orderId}
                  isOpenedInOrderPreviewModal={isOpenedInOrderPreviewModal}
                />
              )}

              {previousRecurrentOrder && nextRecurrentOrder && (
                <FiberManualRecord sx={{ fontSize: 6 }} />
              )}

              {nextRecurrentOrder && (
                <ReccurentOrderDate
                  testId="NextRecurrence"
                  label={OrderTexts.getNextReccurentOrderLabelText()}
                  linkText={nextLinkText}
                  onClick={() => handleRecurrentClick(nextRecurrentOrder)}
                  disabled={!nextRecurrentOrder.orderId}
                  isOpenedInOrderPreviewModal={isOpenedInOrderPreviewModal}
                />
              )}
            </Stack>
          </Stack>
        </preview.Value>
      ) : null}
    </Stack>
  )
}

const ReccurentOrderDate = (props: ReccurentOrderDateProps) => {
  const { label, linkText, onClick, disabled, testId, isOpenedInOrderPreviewModal } = props

  const getTestId = (baseId: string) =>
    isOpenedInOrderPreviewModal ? `orderPreviewModal__${baseId}` : baseId

  const dateTestId = getTestId(`${testId}-Date`)
  const dateTrackId = getTestId(`${testId}-Date`)
  const containerTestId = getTestId(testId || '')

  return (
    <Stack direction="row" alignItems="center" spacing={1} data-testid={containerTestId}>
      <Typography fontSize={12}>{label}:</Typography>

      <Link
        data-testid={dateTestId}
        data-trackid={dateTrackId}
        onClick={() => {
          if (!disabled && onClick) onClick()
        }}
        sx={{
          fontSize: 12,
          cursor: disabled ? 'auto' : 'pointer',
          color: disabled ? 'text.disabled' : 'primary.main',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {linkText}
      </Link>
    </Stack>
  )
}
